import { useEffect, useState } from "react";
import api, { formatTL, formatDateTime } from "../../../lib/api";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../../components/ui/dialog";
import { TrendingUp, TrendingDown, Wallet, HandCoins, Plus, Banknote, Pencil, Trash2, Lock, Download, Search } from "lucide-react";
import { ConfirmDialog } from "../../../components/common";
import { toast } from "sonner";

const TYPE_LABELS = {
  gelir: { label: "Gelir", color: "text-emerald-700 bg-emerald-50" },
  gider: { label: "Gider", color: "text-rose-700 bg-rose-50" },
  teknisyen_borc: { label: "Tek. Tahsilat Borcu", color: "text-amber-700 bg-amber-50" },
  teknisyen_prim: { label: "Tek. Prim Hakedişi", color: "text-violet-700 bg-violet-50" },
  teknisyen_odeme: { label: "Tek. Şirkete Ödeme", color: "text-blue-700 bg-blue-50" },
  manuel: { label: "Manuel", color: "text-slate-700 bg-slate-100" },
};

export default function Ledger() {
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [filterTech, setFilterTech] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(null);
  const [payAmount, setPayAmount] = useState(0);
  const [form, setForm] = useState({ type: "gider", amount: 0, technician_id: "", description: "", date: "" });
  const [accounts, setAccounts] = useState([]);
  const [cats, setCats] = useState({ income: [], expense: [], payment_methods: [] });
  useEffect(() => { api.get("/finance/accounts").then((r) => setAccounts(r.data)); api.get("/finance/categories").then((r) => setCats(r.data)); }, []);

  const dateParams = () => ({ ...(dateFrom ? { date_from: dateFrom } : {}), ...(dateTo ? { date_to: dateTo } : {}) });
  const load = async () => {
    const dp = dateParams();
    const [s, t, te] = await Promise.all([api.get("/cash/summary", { params: dp }), api.get("/cash/transactions", { params: dp }), api.get("/technicians")]);
    setSummary(s.data); setTxs(t.data); setTechs(te.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  const EMPTY = { type: "gider", amount: 0, technician_id: "", description: "", date: "", account_id: "", category: "", payment_method: "", party: "", receipt_no: "" };
  const openCreate = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({ type: t.type, amount: t.amount, technician_id: t.technician_id || "", description: t.description || "", date: (t.date || "").slice(0, 16), account_id: t.account_id || "", category: t.category || "", payment_method: t.payment_method || "", party: t.party || "", receipt_no: t.receipt_no || "" });
    setOpen(true);
  };
  const remove = async () => {
    try { await api.delete(`/cash/transactions/${del.id}`); toast.success("Hareket silindi"); setDel(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); setDel(null); }
  };
  const exportXlsx = async () => {
    const res = await api.get("/export-xlsx/cash", { params: { ...dateParams(), ...(filterType !== "all" ? { status: filterType } : {}), ...(filterTech !== "all" ? { technician_id: filterTech } : {}) }, responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = "kasa-hareketleri.xlsx"; a.click();
  };

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Geçerli tutar girin"); return; }
    try {
      if (editId) await api.put(`/cash/transactions/${editId}`, { ...form, amount: Number(form.amount) });
      else await api.post("/cash/transactions", { ...form, amount: Number(form.amount) });
      toast.success(editId ? "Hareket güncellendi" : "Hareket kaydedildi");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const collectFromTech = async () => {
    if (!payOpen || !payAmount || Number(payAmount) <= 0) return;
    await api.post("/cash/transactions", {
      type: "teknisyen_odeme",
      amount: Number(payAmount),
      technician_id: payOpen.id,
      description: `${payOpen.name} - şirkete tahsilat teslimi`
    });
    toast.success("Tahsilat alındı"); setPayOpen(null); setPayAmount(0); load();
  };

  const filtered = txs.filter(t => {
    if (filterType !== "all" && t.type !== filterType) return false;
    if (filterTech !== "all" && t.technician_id !== filterTech) return false;
    if (q && !`${t.description || ""} ${t.job_number || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  if (!summary) return <div>Yükleniyor...</div>;

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="text-sm text-slate-500">Tüm gelir, gider ve tahsilat hareketleri. Servis kaynaklı kayıtlar servis kaydından yönetilir.</div>
        <div className="flex gap-2 flex-wrap items-end">
          <Button variant="outline" onClick={exportXlsx} data-testid="export-cash"><Download className="h-4 w-4 mr-2" />Excel</Button>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="new-tx-button"><Plus className="h-4 w-4 mr-2" /> Gelir / Gider Ekle</Button>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editId ? "Hareketi Düzenle" : "Manuel Kasa Hareketi"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Tip *</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="tx-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gelir">Gelir</SelectItem>
                    <SelectItem value="gider">Gider</SelectItem>
                    <SelectItem value="teknisyen_odeme">Teknisyenden Tahsilat</SelectItem>
                    <SelectItem value="manuel">Manuel</SelectItem>
                    {editId && <><SelectItem value="teknisyen_borc">Tek. Tahsilat Borcu</SelectItem><SelectItem value="teknisyen_prim">Tek. Prim Hakedişi</SelectItem></>}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Tutar (₺) *</Label><Input type="number" data-testid="tx-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              {form.type.startsWith("teknisyen_") && (
                <div>
                  <Label>Teknisyen</Label>
                  <Select value={form.technician_id} onValueChange={(v) => setForm({ ...form, technician_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                    <SelectContent>{techs.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {(form.type === "gelir" || form.type === "gider") && (<>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Kategori</Label>
                    <Select value={form.category || "_"} onValueChange={(v) => setForm({ ...form, category: v === "_" ? "" : v })}><SelectTrigger data-testid="tx-category"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="_">Kategorisiz</SelectItem>{(form.type === "gelir" ? cats.income : cats.expense).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Hesap</Label>
                    <Select value={form.account_id || "_"} onValueChange={(v) => setForm({ ...form, account_id: v === "_" ? "" : v })}><SelectTrigger data-testid="tx-account"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="_">Varsayılan hesap</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Ödeme şekli</Label>
                    <Select value={form.payment_method || "_"} onValueChange={(v) => setForm({ ...form, payment_method: v === "_" ? "" : v })}><SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="_">Belirtilmedi</SelectItem>{(cats.payment_methods || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Kişi / firma</Label><Input value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} /></div>
                </div>
                <div><Label>Fiş / fatura no</Label><Input value={form.receipt_no} onChange={(e) => setForm({ ...form, receipt_no: e.target.value })} /></div>
              </>)}
              <div><Label>Açıklama</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>Tarih (boşsa şimdi)</Label><Input type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="tx-date-input" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button onClick={save} className="bg-blue-600 hover:bg-blue-700">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border border-slate-200 shadow-none flex flex-wrap items-end gap-3" data-testid="acc-date-filter">
        <div><Label className="text-xs">Başlangıç</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-44" data-testid="acc-date-from" /></div>
        <div><Label className="text-xs">Bitiş</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-44" data-testid="acc-date-to" /></div>
        {[["Bugün", 0], ["Son 7 gün", 6], ["Son 30 gün", 29]].map(([l, n]) => (
          <Button key={l} variant="outline" size="sm" onClick={() => { const d = new Date(); const f = new Date(Date.now() - n * 864e5); const iso = (x) => new Date(x.getTime() - x.getTimezoneOffset() * 6e4).toISOString().slice(0, 10); setDateFrom(iso(f)); setDateTo(iso(d)); }}>{l}</Button>
        ))}
        {(dateFrom || dateTo) && <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }}>Tarihi temizle</Button>}
        <span className="text-xs text-slate-500 ml-auto">{dateFrom || dateTo ? "Gelir/gider/kâr seçili tarih aralığı içindir; usta bakiyeleri tüm zamanları gösterir." : "Tüm zamanlar"}</span>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 border border-slate-200 shadow-none">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-slate-500 font-bold">Toplam Gelir</span><TrendingUp className="h-4 w-4 text-emerald-500" /></div>
          <div className="font-display text-2xl font-bold text-slate-900 mt-2">{formatTL(summary.income)}</div>
        </Card>
        <Card className="p-5 border border-slate-200 shadow-none">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-slate-500 font-bold">Gider</span><TrendingDown className="h-4 w-4 text-rose-500" /></div>
          <div className="font-display text-2xl font-bold text-slate-900 mt-2">{formatTL(summary.expense)}</div>
        </Card>
        <Card className="p-5 border border-slate-200 shadow-none">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-slate-500 font-bold">Net Kâr</span><Wallet className="h-4 w-4 text-blue-600" /></div>
          <div className={`font-display text-2xl font-bold mt-2 ${summary.net_profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatTL(summary.net_profit)}</div>
        </Card>
        <Card className="p-5 border border-amber-200 shadow-none bg-amber-50/40">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wider text-amber-800 font-bold">Teknisyenlerden Alacak</span><HandCoins className="h-4 w-4 text-amber-500" /></div>
          <div className="font-display text-2xl font-bold text-amber-700 mt-2">{formatTL(summary.technician_receivable)}</div>
        </Card>
      </div>

      <Tabs defaultValue="balances">
        <TabsList>
          <TabsTrigger value="balances" data-testid="tab-balances">Teknisyen Bakiyeleri (Borç)</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Tüm Hareketler ({txs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="balances">
          <Card className="border border-slate-200 shadow-none overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Teknisyen</th>
                  <th className="px-4 py-3 font-semibold">Durum</th>
                  <th className="px-4 py-3 font-semibold text-right">Toplam Tahsilat Borcu</th>
                  <th className="px-4 py-3 font-semibold text-right">Şirkete Ödenen</th>
                  <th className="px-4 py-3 font-semibold text-right">Toplam Prim</th>
                  <th className="px-4 py-3 font-semibold text-right">Kalan Borç</th>
                  <th className="px-4 py-3 font-semibold text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {summary.technician_balances.map(b => (
                  <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-medium text-slate-900">{b.name}</td>
                    <td className="px-4 py-3"><span className={`status-pill ${b.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{b.is_active ? "Aktif" : "Pasif"}</span></td>
                    <td className="px-4 py-3 text-right font-mono text-rose-700">{formatTL(b.debt)}</td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-700">{formatTL(b.paid)}</td>
                    <td className="px-4 py-3 text-right font-mono text-violet-700">{formatTL(b.commission)}</td>
                    <td className={`px-4 py-3 text-right font-mono font-bold ${b.balance > 0 ? "text-amber-700" : "text-slate-500"}`}>{formatTL(b.balance)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" disabled={b.balance <= 0} onClick={() => { setPayOpen(b); setPayAmount(b.balance); }} data-testid={`pay-tech-${b.id}`}>
                        <Banknote className="h-3.5 w-3.5 mr-1" /> Tahsilat Al
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="transactions">
          <Card className="p-4 mb-4 border border-slate-200 shadow-none flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Açıklama veya iş no ara..." className="pl-9" data-testid="tx-search" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[220px]" data-testid="tx-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Tipler</SelectItem>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterTech} onValueChange={setFilterTech}>
              <SelectTrigger className="w-[200px]" data-testid="tx-tech-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm ustalar</SelectItem>
                {techs.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Card>
          <Card className="border border-slate-200 shadow-none overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Tarih</th>
                  <th className="px-4 py-3 font-semibold">Tip</th>
                  <th className="px-4 py-3 font-semibold">Açıklama</th>
                  <th className="px-4 py-3 font-semibold text-right">Tutar</th>
                  <th className="px-4 py-3 font-semibold text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-500">Hareket yok</td></tr>}
                {filtered.map(t => (
                  <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-3 text-slate-600">{formatDateTime(t.date)}</td>
                    <td className="px-4 py-3"><span className={`status-pill ${TYPE_LABELS[t.type]?.color}`}>{TYPE_LABELS[t.type]?.label}</span></td>
                    <td className="px-4 py-3 text-slate-700">{t.description}{(t.category || t.party) && <div className="text-xs text-slate-400">{[t.category, t.party, t.payment_method].filter(Boolean).join(" • ")}</div>}</td>
                    <td className={`px-4 py-3 text-right font-mono font-bold ${t.type === "gelir" ? "text-emerald-700" : t.type === "gider" ? "text-rose-700" : "text-slate-900"}`}>
                      {t.type === "gider" ? "-" : "+"}{formatTL(t.amount)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {t.job_id ? (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400" title="Servis kaydından otomatik oluştu; servis kaydını düzenleyin"><Lock className="h-3 w-3" /> Servis</span>
                      ) : (<>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-tx-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setDel(t)} data-testid={`delete-tx-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Tahsilat alma diyalog */}
      <Dialog open={!!payOpen} onOpenChange={(o) => { if (!o) { setPayOpen(null); setPayAmount(0); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Teknisyenden Tahsilat Al</DialogTitle></DialogHeader>
          {payOpen && (
            <div className="space-y-3">
              <div className="p-3 rounded bg-slate-50 border border-slate-200">
                <div className="text-sm text-slate-600">Teknisyen</div>
                <div className="font-semibold text-slate-900">{payOpen.name}</div>
                <div className="text-xs text-slate-500 mt-1">Mevcut borç: <span className="font-mono font-bold text-amber-700">{formatTL(payOpen.balance)}</span></div>
              </div>
              <div>
                <Label>Tahsilat Tutarı (₺) *</Label>
                <Input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="collect-amount-input" />
                <div className="text-xs text-slate-500 mt-1">Kısmi ödeme yapılabilir.</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPayOpen(null); setPayAmount(0); }}>İptal</Button>
            <Button onClick={collectFromTech} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-collect-button">Tahsil Et</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title="Kasa hareketi silinsin mi?"
        description={del ? `${TYPE_LABELS[del.type]?.label} • ${formatTL(del.amount)} • ${del.description || ""}` : ""} onConfirm={remove} />
    </div>
  );
}
